/**
 * What an employee COSTS per hour, for job costing. This app does not run payroll.
 *
 * Board #452, operator ruling 2026-09-17: "we're not a payroll company, this is just for
 * tracking purposes — industry standard or better, match Jobber and such."
 *
 * THE MODEL. Jobber and HouseCall Pro both cost jobs from ONE number per person: an hourly
 * labour cost applied to tracked time. Neither asks for a pay type, because pay type is a
 * payroll concept and payroll is not what either tool is doing. So `employees.hourly_rate`
 * stays the single canonical cost-per-hour here, and every consumer keeps reading that one
 * column — `Labor.js` costs all time from it (lines 135 and 168) regardless of pay_type.
 *
 * WHAT WAS BROKEN. The form offered Hourly, Salary and Piece Rate while the schema had only
 * hourly_rate and overtime_rate. Picking Salary stored an hourly number under a salary label;
 * picking Piece Rate stored one with no piece-rate model behind it at all. Labels without
 * storage, and a job cost quietly computed from the wrong basis.
 *
 * THE "OR BETTER" PART. A salaried person still has an hourly cost — it is just arithmetic
 * nobody should have to do by hand. Enter the salary, and the hourly cost rate is derived and
 * shown before saving. `annual_salary` keeps what was actually typed so re-opening the form
 * shows their salary rather than a divided number they never entered, which is the half that a
 * derive-on-read design gets wrong.
 *
 * Piece rate is deliberately NOT offered. It needs a units-and-amount model that does not
 * exist, and it is rare in these trades — better absent than mislabelled. Measured live
 * 2026-09-17: 0 employees had ever been saved as salary or piece_rate, so removing it stranded
 * nothing.
 */

/**
 * 40 hours x 52 weeks. The conventional full-time year used for exactly this conversion, and
 * what a contractor comparing against Jobber would expect. Named rather than inlined so the
 * form can show the divisor it used — a derived cost rate with no stated basis is a number
 * people cannot check.
 */
export const STANDARD_ANNUAL_HOURS = 2080;

/**
 * May this user see and set what people are paid? Board #451.
 *
 * The Employees route was gated only by the broad `employees` module, which manager and
 * supervisor both get by default — so anyone allowed to maintain a team member's phone number
 * could also read and edit every pay rate in the company. Rates feed Labor and Reports
 * profitability; this is internal payroll data, not a customer-facing billing rate.
 *
 * `payroll` is the EXISTING permission for it — `EMPLOYEE_MODULES.PAYROLL`, backed by the live
 * `employee_permissions.payroll` column, `defaultEnabled: false`, granted to accountants by
 * role default. So this gate reuses the owner instead of inventing a second one. Measured live
 * 2026-09-17: 2 of 32 permission rows have it.
 *
 * Owners and admins keep access without needing the flag, because they are the ones who grant
 * it. Case-insensitive on purpose — `user.role` arrives UPPERCASE from UserContext's mapped
 * path while the raw DB value is lowercase, and board #454 was that exact assumption failing.
 *
 * SCOPE, stated rather than implied: this governs the UI and what the edit payload SENDS. It
 * is now ALSO a server-side boundary (2026-10-07): the pay columns live in
 * `employee_compensation`, whose RLS only exposes them to owner/admin/manager/accountant and
 * the employee's own row, so the embed comes back null for everyone else.
 */
export const canSeeCompensation = (user) => {
  if (!user) return false;
  const roles = [user.role, user.db_role].filter(Boolean).map((r) => String(r).toLowerCase());
  if (roles.some((r) => r === 'owner' || r === 'admin' || r === 'app_owner')) return true;
  return user.permissions?.payroll === true;
};

/**
 * May this user WRITE an `employee_compensation` row (pay, DOB, PTO balance)? Mirrors the DB
 * function `can_manage_employee_compensation()` that the table's INSERT/UPDATE policies
 * require: owner/admin/manager/accountant, APP_OWNER, or `employee_permissions.payroll`.
 * Never the employee themself (20261007_employee_compensation_split.sql), so My Profile
 * must not offer a technician a Date of Birth save that 42501s.
 */
export const canManageCompensation = (user) => {
  if (!user) return false;
  // The RAW db role, as the DB function reads it. `user.role` is the flattened permission role
  // (UserContext maps accountant AND sales_rep to ADMIN), so it would let a sales_rep through.
  const role = String(user.db_role || user.role || '').toLowerCase();
  if (['owner', 'admin', 'manager', 'accountant', 'app_owner'].includes(role)) return true;
  return user.permissions?.payroll === true;
};

/** The columns this gate protects, so the UI and the payload cannot drift apart. */
export const COMPENSATION_FIELDS = ['pay_type', 'hourly_rate', 'overtime_rate', 'annual_salary'];

/**
 * The employee's overtime pay rate for TRACKING (#4210 — the app does not do payroll;
 * the customer is billed via company_settings.overtime_multiplier, a different number).
 * Only an UNSET overtime_rate (null / undefined / '') means "default to 1.5x hourly".
 * A stored 0.00 is a deliberate 0 and is returned as 0 — the old `|| hourly * 1.5`
 * fallback silently turned those rows into 1.5x.
 */
export const resolveOvertimeRate = (overtimeRate, hourlyRate) => {
  if (overtimeRate === null || overtimeRate === undefined || overtimeRate === '') {
    return (parseFloat(hourlyRate) || 0) * 1.5;
  }
  const parsed = parseFloat(overtimeRate);
  return Number.isNaN(parsed) ? (parseFloat(hourlyRate) || 0) * 1.5 : parsed;
};

/**
 * The columns that LEFT `employees` for the 1:1 `employee_compensation` table (PK employee_id).
 * App roles can no longer select or write them on `employees` (42501). Reads embed them
 * (`employee_compensation(...)`) and writes upsert the side table — `splitCompensationPayload`
 * is the one place that knows which keys go where.
 */
export const SENSITIVE_EMPLOYEE_FIELDS = [
  'hourly_rate', 'overtime_rate', 'annual_salary',
  'social_security_number', 'date_of_birth', 'pto_balance_hours',
];

/**
 * The `employees` columns an app role may still select, verified live 2026-10-07 against
 * information_schema minus the six sensitive ones. `select('*')` now 42501s, so every
 * full-row read must spell these out.
 */
export const EMPLOYEE_SAFE_COLUMNS = [
  'id', 'company_id', 'user_id', 'employee_number', 'hire_date', 'termination_date',
  'job_title', 'department', 'emergency_contact_name', 'emergency_contact_phone',
  'certifications', 'skills', 'notes', 'created_at', 'updated_at', 'employee_type',
  'pay_type', 'is_schedulable', 'first_name', 'last_name', 'capacity_hours_per_day',
  'manager_id', 'emergency_contact_relationship', 'status', 'created_by',
].join(',');

/** The embed clause for the non-SSN compensation columns. SSN is only selected where shown. */
export const EMPLOYEE_COMP_EMBED =
  'employee_compensation(hourly_rate,overtime_rate,annual_salary,date_of_birth,pto_balance_hours)';

/** The embedded compensation object, or {} when the caller is not allowed to see it (null). */
export const getComp = (employee) => employee?.employee_compensation || {};

/**
 * Flatten the embed back onto the row so consumers keyed on `employee.hourly_rate` etc. keep
 * working. Applied where rows are LOADED (useEmployeesPageData, MyProfile), not per consumer.
 */
export const withComp = (employee) => {
  if (!employee) return employee;
  const comp = getComp(employee);
  const flat = { ...employee };
  SENSITIVE_EMPLOYEE_FIELDS.forEach((field) => {
    if (comp[field] !== undefined) flat[field] = comp[field];
  });
  return flat;
};

/**
 * Split an employees write payload into the part that still belongs on `employees` and the
 * part that must be upserted to `employee_compensation`. `comp` is null when no sensitive key
 * was present, so callers can skip the second write entirely.
 */
export const splitCompensationPayload = (payload = {}) => {
  const employee = { ...payload };
  const comp = {};
  let any = false;
  SENSITIVE_EMPLOYEE_FIELDS.forEach((field) => {
    if (field in employee) {
      comp[field] = employee[field];
      delete employee[field];
      any = true;
    }
  });
  return { employee, comp: any ? comp : null };
};

/** Write the compensation side table. Safe to call after any employees write (row exists 1:1). */
export const upsertEmployeeCompensation = async (supabase, employeeId, companyId, comp) => {
  if (!comp || !employeeId) return { error: null };
  return supabase
    .from('employee_compensation')
    .upsert({ employee_id: employeeId, company_id: companyId, ...comp }, { onConflict: 'employee_id' });
};

/**
 * Strip compensation from an update payload for a user who may not set it.
 *
 * Omitting a key leaves the stored column untouched, so a manager editing somebody's phone
 * number cannot blank their pay rate as a side effect of a form that never showed it to them —
 * which is what would happen if the hidden fields were simply submitted empty.
 */
export const withoutCompensation = (payload = {}) => {
  const next = { ...payload };
  COMPENSATION_FIELDS.forEach((field) => { delete next[field]; });
  return next;
};

/** The pay types the app can actually STORE and cost. Piece rate is absent by decision. */
export const PAY_TYPES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'salary', label: 'Salary' },
];

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/** Salary -> cost per hour. Returns 0 for anything that is not a usable positive salary. */
export const hourlyCostFromSalary = (annualSalary) => {
  const salary = toNumber(annualSalary);
  if (salary <= 0) return 0;
  return salary / STANDARD_ANNUAL_HOURS;
};

/** Two decimals, matching how the rate is stored and displayed everywhere else. */
export const roundRate = (value) => Math.round(toNumber(value) * 100) / 100;

/**
 * Normalise a compensation form into the columns to write.
 *
 * ONE owner because Employees.js saves from two places (the invite/create path and the edit
 * path) and they must not drift — a salaried employee whose rate is derived on create but not
 * on edit would silently revert to an uncosted 0 the first time anyone edited them.
 *
 * Returns the four compensation columns, always all four, so a pay-type change CLEARS the
 * fields that no longer apply instead of leaving a stale rate behind.
 */
export const resolveCompensation = (formData = {}) => {
  const payType = formData.pay_type || 'hourly';

  if (payType === 'salary') {
    const annualSalary = toNumber(formData.annual_salary);
    return {
      pay_type: 'salary',
      annual_salary: annualSalary > 0 ? annualSalary : null,
      // The derived cost rate IS hourly_rate, so job costing needs no knowledge of pay types.
      hourly_rate: roundRate(hourlyCostFromSalary(annualSalary)),
      // Salaried staff do not accrue an overtime COST in this model: their cost per hour is
      // already the whole-year figure spread over standard hours.
      overtime_rate: 0,
    };
  }

  return {
    pay_type: 'hourly',
    annual_salary: null,
    hourly_rate: roundRate(formData.hourly_rate),
    overtime_rate: roundRate(formData.overtime_rate),
  };
};
